// src/components/VideoPlayer.tsx
import {
  Box,
  CircularProgress,
  IconButton,
  Slider,
  Typography,
  useTheme,
} from "@mui/material";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  TbArrowsMaximize,
  TbPlayerPause,
  TbPlayerPlay,
  TbPlayerTrackNext,
  TbPlayerTrackPrev,
  TbReload,
} from "react-icons/tb";

export interface VideoPlayerHandles {
  seekTo: (time: string) => void; // Formatos admitidos: "HH:MM:SS", "MM:SS", "SS"
}

interface VideoPlayerProps {
  mediaUrl: string | null;
  loading: boolean;
  error: string | null;
  height?: number; // opcional: alto del player embebido
  onToast?: (message: string, type: "success" | "danger" | "info") => void;
}

const timeToSeconds = (timeString: string): number => {
  // Acepta "HH:MM:SS", "MM:SS" o "SS"
  const parts = timeString.split(":").map((p) => Number(p.trim()));
  if (parts.some((n) => Number.isNaN(n))) return 0;

  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parts[0];
};

const formatTime = (s: number) => {
  if (!isFinite(s) || s < 0) s = 0;
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);

  if (h > 0) {
    return `${h}:${m.toString().padStart(2, "0")}:${sec
      .toString()
      .padStart(2, "0")}`;
  }
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const VideoPlayer = forwardRef<VideoPlayerHandles, VideoPlayerProps>(
  ({ mediaUrl, loading, error, height = 180, onToast }, ref) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [playing, setPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const theme = useTheme();

    useImperativeHandle(ref, () => ({
      seekTo(time: string) {
        const video = videoRef.current;
        if (!video || !mediaUrl || video.readyState < 1) {
          onToast?.("El video aún no está listo para navegar.", "info");
          return;
        }
        const seconds = timeToSeconds(time);
        const handleSeeked = () => {
          video.removeEventListener("seeked", handleSeeked);
          // Intentar reproducir tras mover el puntero
          video.play().catch(() => {
            onToast?.(
              "Se navegó en el video, pero no se pudo iniciar la reproducción (posible política del navegador).",
              "info"
            );
          });
        };
        video.pause();
        video.addEventListener("seeked", handleSeeked);
        video.currentTime = seconds;
      },
    }));

    useEffect(() => {
      const video = videoRef.current;
      if (!video) return;

      const handlePlay = () => setPlaying(true);
      const handlePause = () => setPlaying(false);
      const updateProgress = () => setProgress(video.currentTime || 0);
      const updateDuration = () => setDuration(video.duration || 0);

      video.addEventListener("play", handlePlay);
      video.addEventListener("pause", handlePause);
      video.addEventListener("timeupdate", updateProgress);
      video.addEventListener("loadedmetadata", updateDuration);

      return () => {
        video.removeEventListener("play", handlePlay);
        video.removeEventListener("pause", handlePause);
        video.removeEventListener("timeupdate", updateProgress);
        video.removeEventListener("loadedmetadata", updateDuration);
      };
    }, [mediaUrl]);

    const togglePlay = () => {
      const video = videoRef.current;
      if (!video) return;
      if (video.paused) video.play();
      else video.pause();
    };

    const handleSeek = (_: any, value: number) => {
      const video = videoRef.current;
      if (!video) return;
      video.currentTime = value;
      setProgress(value);
    };

    return (
      <Box p={2} bgcolor="background.paper" borderRadius={2} boxShadow={1}>
        {loading && (
          <Box display="flex" alignItems="center" gap={2} height={height}>
            <CircularProgress size={32} />
            <Typography>Cargando video…</Typography>
          </Box>
        )}

        {!loading && error && (
          <Typography color="error" align="center">
            {error}
          </Typography>
        )}

        {!loading && !error && mediaUrl && (
          <Box display="flex" flexDirection={"column"} alignItems="center" gap={2}>
            <Box
              sx={{
                bgcolor: "black",
                borderRadius: 2,
                overflow: "hidden",
                width: "100%",
                height,
              }}
            >
              <video
                ref={videoRef}
                src={mediaUrl}
                preload="metadata"
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
                controls={false}
              />
            </Box>

            <Box
              flex={1}
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              gap={1}
            >
              <Box display="flex" justifyContent="center" gap={1.5}>
                <IconButton
                  onClick={() => {
                    if (videoRef.current) videoRef.current.currentTime = 0;
                  }}
                >
                  <TbReload color={theme.palette.text.disabled} size={22} />
                </IconButton>

                <IconButton
                  onClick={() => {
                    if (videoRef.current)
                      videoRef.current.currentTime =
                        Math.max(0, videoRef.current.currentTime - 10);
                  }}
                >
                  <TbPlayerTrackPrev
                    color={theme.palette.text.disabled}
                    size={22}
                  />
                </IconButton>

                <IconButton
                  onClick={togglePlay}
                  sx={{
                    bgcolor: "primary.main",
                    color: "white",
                    width: 40,
                    height: 40,
                    "&:hover": { bgcolor: "primary.dark" },
                  }}
                >
                  {playing ? (
                    <TbPlayerPause size={16} />
                  ) : (
                    <TbPlayerPlay size={16} />
                  )}
                </IconButton>

                <IconButton
                  onClick={() => {
                    if (videoRef.current)
                      videoRef.current.currentTime =
                        Math.min(
                          duration,
                          (videoRef.current.currentTime || 0) + 10
                        );
                  }}
                >
                  <TbPlayerTrackNext
                    color={theme.palette.text.disabled}
                    size={22}
                  />
                </IconButton>

                <IconButton onClick={() => videoRef.current?.requestFullscreen()}>
                  <TbArrowsMaximize
                    color={theme.palette.text.disabled}
                    size={22}
                  />
                </IconButton>
              </Box>

              <Box
                mt={1}
                display="flex"
                alignItems="center"
                gap={1.5}
                width="100%"
                px={2}
              >
                <Typography variant="body2">{formatTime(progress)}</Typography>
                <Slider
                  value={progress}
                  min={0}
                  max={duration || 0}
                  onChange={handleSeek}
                  sx={{ flex: 1 }}
                />
                <Typography variant="body2">{formatTime(duration)}</Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    );
  }
);

export default VideoPlayer;
